import { NICKNAME_RULES } from './user.js';
/**
 * 활동명 검증. 서버가 최종 판정하지만, 프론트도 같은 규칙으로 미리 보여주기 위해 공용으로 둔다.
 * 규칙(docs/SCHEMA.md): 영문 5자 이상 **또는** 한글 2자 이상 / 최대 20칸(한글 1자=2칸) / 중복 불가.
 */
/** 한글: 자모(U+1100~) + 호환자모(U+3130~) + 음절(U+AC00~) */
const HANGUL = /[\u1100-\u11ff\u3130-\u318f\uac00-\ud7a3]/;
const LATIN = /[A-Za-z]/;
/** 폭 2칸으로 세는 문자: 한글, 가나, CJK, 전각 */
const WIDE = /[\u1100-\u11ff\u3000-\u303f\u3040-\u30ff\u3130-\u318f\u4e00-\u9fff\uac00-\ud7a3\uff00-\uffe6]/;
/** C0/C1 제어문자, 제로폭·방향제어, BOM 등 눈에 보이지 않는 문자 */
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff]/;
/** 표시용 정규화: 유니코드 NFC + 앞뒤 공백 제거 + 연속 공백 1칸으로 축소 */
export function normalizeNickname(raw) {
    return raw.normalize('NFC').trim().replace(/\s+/g, ' ');
}
/** 중복 비교용 키. 정규화 + 소문자 + 공백 제거. */
export function nicknameKey(raw) {
    return normalizeNickname(raw).toLowerCase().replace(/\s/g, '');
}
/** 칸 수 계산. 한글/CJK/전각은 2칸, 나머지는 1칸. */
export function nicknameWidth(value) {
    let width = 0;
    for (const ch of value)
        width += WIDE.test(ch) ? 2 : 1;
    return width;
}
/** 시스템/사칭 방지용 예약 접두사. */
const RESERVED_PREFIXES = ['admin', '운영자', '관리자', '토마토마켓'];
export function checkNickname(raw) {
    const value = normalizeNickname(raw);
    if (!value)
        return { ok: false, value, error: 'empty' };
    if (CONTROL.test(value))
        return { ok: false, value, error: 'invalid_char' };
    if (nicknameWidth(value) > NICKNAME_RULES.maxWidth) {
        return { ok: false, value, error: 'too_wide' };
    }
    let hangul = 0;
    let latin = 0;
    for (const ch of value) {
        if (HANGUL.test(ch))
            hangul += 1;
        else if (LATIN.test(ch))
            latin += 1;
    }
    if (hangul < NICKNAME_RULES.minHangul && latin < NICKNAME_RULES.minLatin) {
        return { ok: false, value, error: 'too_short' };
    }
    const key = nicknameKey(value);
    if (RESERVED_PREFIXES.some((p) => key.startsWith(p))) {
        return { ok: false, value, error: 'reserved' };
    }
    return { ok: true, value };
}
export const NICKNAME_ERROR_MESSAGES = {
    empty: '활동명을 입력해 주세요.',
    too_short: '영문은 5자, 한글은 2자 이상으로 지어 주세요.',
    too_wide: '20칸까지 쓸 수 있어요. (한글 1자 = 2칸)',
    invalid_char: '쓸 수 없는 문자가 들어 있어요.',
    reserved: '쓸 수 없는 활동명이에요.',
};
/**
 * "인디언식" 랜덤 활동명 제안 — 이름 짓기가 막막한 사람을 위한 재미 요소(가입 화면).
 * 수식어(≤4자) + 명사(≤3자) 조합이라 항상 규칙(한글 2자↑, 20칸↓)을 만족한다.
 * 최대 폭: 4자*2 + 공백 1 + 3자*2 = 17칸.
 */
const NICK_MODIFIERS = [
    '춤추는', '노래하는', '고요한', '용감한', '지혜로운', '자유로운', '웃는', '달리는',
    '날아오르는', '조용한', '붉은', '푸른', '신중한', '배고픈', '늦잠자는', '반짝이는',
    '느긋한', '꿈꾸는', '씩씩한', '수줍은', '외로운', '엉뚱한', '따뜻한', '재빠른',
    '노을 진', '별을 센', '비를 부른', '바람 든',
];
const NICK_NOUNS = [
    '늑대', '여우', '곰', '매', '사슴', '호수', '바람', '강', '산', '달빛', '별', '토끼',
    '수달', '부엉이', '다람쥐', '고래', '여울', '소나무', '들소', '독수리', '너구리',
    '순록', '오소리', '두더지', '고슴도치', '반달곰',
];
/** 규칙을 통과하는 인디언식 활동명 하나를 뽑는다. */
export function suggestNickname() {
    const m = NICK_MODIFIERS[Math.floor(Math.random() * NICK_MODIFIERS.length)];
    const n = NICK_NOUNS[Math.floor(Math.random() * NICK_NOUNS.length)];
    return `${m} ${n}`;
}
//# sourceMappingURL=nickname.js.map